// "On Site" — aggregate, prominent view of who is currently signed in/out across a
// client's whole estate (all sites/projects), with manual sign-in/out/correction
// and a filterable history. PR staff get a client selector (mirrors Inductions);
// portal users (client_admin/site_manager/reviewer) are scoped to their own client.
import { useEffect, useState, useCallback, useMemo } from "react";
import {
    Radar, LogOut, Pencil, AlertTriangle, Mail, Phone, Loader2, RefreshCw, Building2, MapPin, Download,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDT, isToday, daysSince, personTypeDisplay } from "@/lib/onsiteFormat";
import { ManualSignInDialog, SignOutDialog, SignInButton } from "@/components/onsite/OnSiteDialogs";

const BRAND = "var(--brand-accent, #C75A1B)";
const INTERNAL = new Set(["director", "administrator", "consultant"]);

export default function OnSitePage() {
    const { user } = useAuth();
    const isInternal = INTERNAL.has(user?.role);
    const canEdit = user?.permissions?.inductions === "E";

    const [clients, setClients] = useState([]);
    const [clientId, setClientId] = useState(isInternal ? "" : (user?.client_id || (user?.client_ids || [])[0] || ""));
    const [sites, setSites] = useState([]);
    const [projects, setProjects] = useState([]);
    const [siteId, setSiteId] = useState("");
    const [projectId, setProjectId] = useState("");

    const [onSite, setOnSite] = useState([]);
    const [loadingNow, setLoadingNow] = useState(false);
    const [history, setHistory] = useState([]);
    const [loadingHist, setLoadingHist] = useState(false);
    const today = new Date().toISOString().slice(0, 10);
    const weekAgo = new Date(Date.now() - 6 * 86400000).toISOString().slice(0, 10);
    const [dateFrom, setDateFrom] = useState(weekAgo);
    const [dateTo, setDateTo] = useState(today);

    const [signInOpen, setSignInOpen] = useState(false);
    const [signOutRec, setSignOutRec] = useState(null); // {id, name, time_out, correcting}

    useEffect(() => {
        if (isInternal) api.get("/clients").then((r) => {
            setClients(r.data || []);
            if (!clientId && (r.data || []).length) setClientId(r.data[0].id);
        }).catch(() => {});
    }, [isInternal]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!clientId) return;
        api.get(`/sites?client_id=${clientId}`).then((r) => setSites(r.data || [])).catch(() => setSites([]));
        api.get(`/projects?client_id=${clientId}`).then((r) => setProjects(r.data || [])).catch(() => setProjects([]));
        setSiteId(""); setProjectId("");
    }, [clientId]);

    const filterParams = useCallback((extra = {}) => {
        const p = new URLSearchParams({ client_id: clientId, ...extra });
        if (siteId) p.set("site_id", siteId);
        if (projectId) p.set("project_id", projectId);
        return p;
    }, [clientId, siteId, projectId]);

    const loadOnSite = useCallback(() => {
        if (!clientId) return;
        setLoadingNow(true);
        api.get(`/site-forms/attendance?${filterParams({ open_only: "true" }).toString()}`)
            .then((r) => setOnSite(r.data || [])).catch(() => setOnSite([])).finally(() => setLoadingNow(false));
    }, [clientId, filterParams]);

    const loadHistory = useCallback(() => {
        if (!clientId) return;
        setLoadingHist(true);
        api.get(`/site-forms/attendance?${filterParams({ date_from: dateFrom, date_to: dateTo }).toString()}`)
            .then((r) => setHistory(r.data || [])).catch(() => setHistory([])).finally(() => setLoadingHist(false));
    }, [clientId, filterParams, dateFrom, dateTo]);

    useEffect(loadOnSite, [loadOnSite]);
    useEffect(loadHistory, [loadHistory]);

    const flaggedCount = useMemo(() => onSite.filter((r) => !isToday(r.time_in)).length, [onSite]);
    const reload = () => { loadOnSite(); loadHistory(); };

    const exportCsv = () => {
        const headers = ["Name", "Person type", "Company", "Site", "Project", "Signed in", "Signed out"];
        const fmtPt = (r) => personTypeDisplay(r.person_type, r.person_type_other);
        const rows = history.map((r) => [r.name, fmtPt(r), r.company || "", r.site_name || "", r.project_name || "", r.time_in || "", r.time_out || ""]);
        const csv = [headers, ...rows].map((row) => row.map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = `onsite-attendance-${dateFrom}-to-${dateTo}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <div className="space-y-5 pb-10" data-testid="onsite-page">
            <div className="flex items-center gap-3 flex-wrap">
                <div className="w-10 h-10 rounded-xl grid place-items-center text-white" style={{ background: BRAND }}><Radar size={20} /></div>
                <div className="flex-1 min-w-0">
                    <h1 className="text-2xl font-bold tracking-tight text-[#1F1F1F]">On Site</h1>
                    <p className="text-[12.5px] text-[#6F6A64]">Who's signed in right now, anywhere across the estate — and who forgot to sign out.</p>
                </div>
                {isInternal && (
                    <select data-testid="onsite-client-select" value={clientId} onChange={(e) => setClientId(e.target.value)}
                        className="h-10 px-3 rounded-lg border text-[13px] bg-white" style={{ borderColor: "#E5E0DA", minWidth: 220 }}>
                        <option value="">— Select a client —</option>
                        {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                )}
            </div>

            {!clientId ? (
                <Empty msg="Select a client to see who's on site." />
            ) : (
                <>
                    <div className="flex flex-wrap items-end gap-3">
                        <label className="text-[12px] font-semibold text-[#1F1F1F]">Site
                            <select data-testid="onsite-site-filter" value={siteId} onChange={(e) => setSiteId(e.target.value)}
                                className="ml-2 h-9 px-2.5 rounded-lg border text-[13px] bg-white" style={{ borderColor: "#E5E0DA" }}>
                                <option value="">All sites</option>
                                {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                            </select>
                        </label>
                        <label className="text-[12px] font-semibold text-[#1F1F1F]">Project
                            <select data-testid="onsite-project-filter" value={projectId} onChange={(e) => setProjectId(e.target.value)}
                                className="ml-2 h-9 px-2.5 rounded-lg border text-[13px] bg-white" style={{ borderColor: "#E5E0DA" }}>
                                <option value="">All projects</option>
                                {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                            </select>
                        </label>
                        <button data-testid="onsite-refresh" onClick={reload} className="h-9 w-9 grid place-items-center rounded-lg border hover:bg-[#F6F4F0]" style={{ borderColor: "#E5E0DA" }} title="Refresh">
                            <RefreshCw size={14} className="text-[#6F6A64]" />
                        </button>
                        {canEdit && <div className="ml-auto"><SignInButton onClick={() => setSignInOpen(true)} /></div>}
                    </div>

                    <div className="grid sm:grid-cols-2 gap-3">
                        <Metric testid="onsite-metric-now" label="On site now" value={onSite.length} tone={onSite.length ? "green" : "muted"} />
                        <Metric testid="onsite-metric-flagged" label="Hasn't signed out (1+ day)" value={flaggedCount} tone={flaggedCount ? "red" : "muted"} />
                    </div>

                    <div className="bg-white border rounded-[13px] shadow-brand-sm overflow-hidden" style={{ borderColor: "#E5E0DA" }} data-testid="onsite-now-panel">
                        <div className="px-5 py-3.5 flex items-center gap-2 border-b" style={{ borderColor: "#F0EDE8" }}>
                            <h3 className="text-[13.5px] font-semibold flex-1">On site now ({onSite.length})</h3>
                            {loadingNow && <Loader2 size={14} className="animate-spin text-[#9A968F]" />}
                        </div>
                        <div className="p-2">
                            {onSite.length === 0 ? <p className="text-[12px] text-[#9A968F] text-center py-6">Nobody currently signed in for this scope.</p> : (
                                <ul className="divide-y" style={{ borderColor: "#F5F3F0" }} data-testid="onsite-now-list">
                                    {onSite.map((r) => {
                                        const flagged = !isToday(r.time_in);
                                        return (
                                            <li key={r.id} data-testid={`onsite-row-${r.id}`} className="flex items-center gap-3 px-3 py-2.5">
                                                <span className={`w-2 h-2 rounded-full flex-shrink-0 ${flagged ? "bg-red-500" : "bg-green-500"}`} />
                                                <div className="min-w-0 flex-1">
                                                    <div className="flex items-center gap-2">
                                                        <span className="font-semibold text-[13px] text-[#1F1F1F] truncate">{r.name}</span>
                                                        {r.person_type && <span data-testid={`onsite-row-persontype-${r.id}`} className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-[#F0EDE8] text-[#6F6A64] flex-shrink-0">{personTypeDisplay(r.person_type, r.person_type_other)}</span>}
                                                        {r.company && <span className="text-[11.5px] text-[#9A968F] truncate">· {r.company}</span>}
                                                        {flagged && (
                                                            <span data-testid={`onsite-flag-${r.id}`} className="inline-flex items-center gap-1 text-[10.5px] font-semibold px-1.5 py-0.5 rounded-full bg-red-50 text-red-600">
                                                                <AlertTriangle size={10} /> {daysSince(r.time_in)}d — hasn't signed out
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="text-[11px] text-[#6F6A64] flex items-center gap-2.5 mt-0.5 flex-wrap">
                                                        <span>Signed in {fmtDT(r.time_in)}</span>
                                                        {r.site_name && <span data-testid={`onsite-row-site-${r.id}`} className="inline-flex items-center gap-1"><MapPin size={10} /> {r.site_name}</span>}
                                                        {r.project_name && <span data-testid={`onsite-row-project-${r.id}`} className="inline-flex items-center gap-1"><Building2 size={10} /> {r.project_name}</span>}
                                                        {r.email && <span className="inline-flex items-center gap-1"><Mail size={10} /> {r.email}</span>}
                                                        {r.mobile && <span className="inline-flex items-center gap-1"><Phone size={10} /> {r.mobile}</span>}
                                                        {r.manual_entry && <span className="italic">Manually signed in by {r.signed_in_by_name || "staff"}</span>}
                                                    </div>
                                                </div>
                                                {canEdit && (
                                                    <button data-testid={`onsite-signout-${r.id}`} onClick={() => setSignOutRec({ id: r.id, name: r.name, time_out: null })}
                                                        className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-[7px] text-[11.5px] font-semibold border hover:bg-[#F6F4F0]" style={{ borderColor: "#E5E0DA" }}>
                                                        <LogOut size={12} /> Sign out
                                                    </button>
                                                )}
                                            </li>
                                        );
                                    })}
                                </ul>
                            )}
                        </div>
                    </div>

                    <div className="bg-white border rounded-[13px] shadow-brand-sm overflow-hidden" style={{ borderColor: "#E5E0DA" }} data-testid="onsite-history-panel">
                        <div className="px-5 py-3.5 flex items-center gap-2.5 border-b flex-wrap" style={{ borderColor: "#F0EDE8" }}>
                            <h3 className="text-[13.5px] font-semibold">History</h3>
                            <div className="flex items-center gap-1.5 ml-auto text-[12px]">
                                <input type="date" data-testid="onsite-history-from" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-8 px-2 rounded-md border text-[12px]" style={{ borderColor: "#E5E0DA" }} />
                                <span className="text-[#9A968F]">to</span>
                                <input type="date" data-testid="onsite-history-to" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-8 px-2 rounded-md border text-[12px]" style={{ borderColor: "#E5E0DA" }} />
                            </div>
                            <button data-testid="onsite-export-csv" onClick={exportCsv} disabled={!history.length}
                                className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-md border text-[12px] font-semibold hover:bg-[#F6F4F0] disabled:opacity-50" style={{ borderColor: "#E5E0DA" }}>
                                <Download size={12} /> Export CSV
                            </button>
                            {loadingHist && <Loader2 size={14} className="animate-spin text-[#9A968F]" />}
                        </div>
                        <div className="overflow-x-auto">
                            {history.length === 0 ? <p className="text-[12px] text-[#9A968F] text-center py-6">No attendance recorded in this range.</p> : (
                                <table className="w-full text-[12.3px]" data-testid="onsite-history-table">
                                    <thead>
                                        <tr className="text-left text-[10.5px] uppercase tracking-wide text-[#9A968F] border-b" style={{ borderColor: "#F0EDE8" }}>
                                            <th className="px-4 py-2">Name</th><th className="px-4 py-2">Person type</th><th className="px-4 py-2">Company</th>
                                            <th className="px-4 py-2">Site</th><th className="px-4 py-2">Project</th>
                                            <th className="px-4 py-2">Signed in</th>
                                            <th className="px-4 py-2">Signed out</th><th className="px-4 py-2" />
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {history.map((r) => (
                                            <tr key={r.id} data-testid={`onsite-history-row-${r.id}`} className="border-b last:border-b-0" style={{ borderColor: "#F5F3F0" }}>
                                                <td className="px-4 py-2 font-medium text-[#1F1F1F]">{r.name}</td>
                                                <td className="px-4 py-2 text-[#6F6A64]">{r.person_type ? personTypeDisplay(r.person_type, r.person_type_other) : "—"}</td>
                                                <td className="px-4 py-2 text-[#6F6A64]">{r.company || "—"}</td>
                                                <td className="px-4 py-2 text-[#6F6A64]">{r.site_name || "—"}</td>
                                                <td className="px-4 py-2 text-[#6F6A64]">{r.project_name || "—"}</td>
                                                <td className="px-4 py-2 text-[#6F6A64]">{fmtDT(r.time_in)}</td>
                                                <td className="px-4 py-2">
                                                    {r.time_out ? (
                                                        <span className="text-[#6F6A64]">{fmtDT(r.time_out)}{r.edited_at && <span className="italic text-[10px] ml-1">(corrected)</span>}</span>
                                                    ) : <span className="text-green-600 font-semibold text-[11px]">On site</span>}
                                                </td>
                                                <td className="px-4 py-2 text-right">
                                                    {canEdit && (
                                                        <button data-testid={`onsite-history-edit-${r.id}`} onClick={() => setSignOutRec({ id: r.id, name: r.name, time_out: r.time_out })}
                                                            className="text-[#9A968F] hover:text-[#1F1F1F]" title={r.time_out ? "Correct sign-out time" : "Sign out"}>
                                                            <Pencil size={13} />
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </div>
                </>
            )}

            {signInOpen && (
                <ManualSignInDialog clientId={clientId} sites={sites} projects={projects}
                    defaultSiteId={siteId} defaultProjectId={projectId}
                    onClose={() => setSignInOpen(false)} onDone={() => { setSignInOpen(false); reload(); }} />
            )}
            {signOutRec && (
                <SignOutDialog rec={signOutRec} onClose={() => setSignOutRec(null)} onDone={() => { setSignOutRec(null); reload(); }} />
            )}
        </div>
    );
}

function Metric({ testid, label, value, tone }) {
    const tones = { green: { bg: "#E8F1E6", fg: "#3F7D3A" }, red: { bg: "#FBE8E5", fg: "#B33A2E" }, muted: { bg: "#F0EDE8", fg: "#6F6A64" } };
    const t = tones[tone] || tones.muted;
    return (
        <div data-testid={testid} className="rounded-[12px] p-4 flex items-center justify-between" style={{ background: t.bg }}>
            <span className="text-[12px] font-semibold" style={{ color: t.fg }}>{label}</span>
            <span className="text-2xl font-bold" style={{ color: t.fg }}>{value}</span>
        </div>
    );
}

function Empty({ msg }) {
    return <div className="bg-white border rounded-[13px] py-14 text-center" style={{ borderColor: "#E5E0DA" }}><p className="text-[13px] text-[#6F6A64]">{msg}</p></div>;
}
